// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Line over time: a Hovmöller diagram in Analyze's Profiles tab
 * (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, slice 3; the store is
 * `services/research/hovmoller.ts`).
 *
 * Recording is automatic, the way the pins fill their graph: while a line
 * is drawn and the panel is open, each frame the globe shows is read along
 * the line and lands in its row. Nothing to start, nothing to forget to
 * stop; the line, Clear, a new dataset, closing the panel or "Clear pins,
 * lines and boxes" end it (`resetHovmoller`).
 *
 * The diagram follows the classic layout: distance along the line across,
 * time running downward (first frame at the top), as in Hovmöller (1949)
 * and the meteorological diagrams since. Colours are the dataset's own
 * palette and range (the display LUT the globe uses), or, when a baseline
 * frame is held, optionally the change from it in the change map's
 * diverging palette with a fixed range.
 *
 * Gated as the other research tools (decision R2): open on Research and
 * Situation Room plans (or `xr-cap-research`); a lock that opens the plans
 * on plans with the data tools; nothing for a class borrowing them.
 */

import {
  buildHovmollerCsvText,
  cellValue,
  changeLimit,
  createRecording,
  lineValueTable,
  quantisationStep,
  recordFrame,
  regrid,
  rowsRead,
  rowTimeMs,
  sameWindow,
  type HovmollerRecording,
  type LinePoint,
} from '../services/research/hovmoller'
import type { TransectSample } from '../services/datasetStats'
import { buildDisplayLut, displayGradientStops, type ColorScaleDisplay } from '../services/colorScaleDisplay'
import { releaseOf, type ColorScale } from '../types/color-scale'
import type { DisplayColorScale } from '../types/unit-scale'
import { CHANGE_GREY, CHANGE_PALETTE, changeColour, niceLimit } from '../services/research/changeMap'
import { baselineValueAt, changeViewState, type BaselineField } from '../services/research/changeView'
import { SIGMA_LIMIT, climatologyAt, periodMethodText } from '../services/research/climatology'
import { latLonToTexelUv } from '../services/datasetProbe'
import { analysisExportsLocked, dataToolsAllowed, dataToolsLent, researchToolsAllowed, researchUpsellShown } from '../services/planAccess'
import { formatStatValue } from './analyzeCharts'
import { downloadCsv } from './analyzeExport'
import { trail, trailFile, trailOn } from '../services/research/researchTrail'
import { ayniCsvFilename } from './pinsCsv'
import { openPlans } from './xrPlansUI'
import { t } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { frameIsoForFile, researchFrameAxis, snapToFrame } from '../services/research/frameTime'
import { fillWhenHost, renderWhenBlock } from './whenExtremesUI'

/** The frame on the globe, as the diagram needs it (the pins' clock). */
export interface HovmollerFrame {
  /** Changes whenever the frame does. */
  id: string
  datasetKey: string
  /** The frame's time and its window; nulls for a still image. */
  timeMs: number | null
  startMs: number | null
  endMs: number | null
  /** Frames in the window, when the stream says. */
  frames: number | null
  /** True when the times are dates (UTC); false for playback time. */
  dated: boolean
}

/** What the diagram needs from the app. */
export interface HovmollerFeed {
  frame(): HovmollerFrame | null
  /** The codes under each point on the frame on screen (-1 outside), and the scale. */
  codesAlong(points: readonly LinePoint[]): { codes: Int16Array; scale: ColorScale } | null
  seek?(timeMs: number): void
  /** The playing video, so every presented frame can be read. */
  video?(): HTMLVideoElement | null
}

export interface HovmollerContext {
  feed: HovmollerFeed | null
  datasetTitle(): string | null
  display(): ColorScaleDisplay
  /** The provenance rows of the view (dataset, link, credit, sources, software, exported_at). */
  provenance?(): readonly (readonly (string | number | null)[])[] | null
  announce?(message: string): void
}

/** The line the diagram follows: the Line profile's own samples and scale. */
export interface HovmollerLine {
  samples: readonly TransectSample[]
  scale: DisplayColorScale
}

/** Values; the change from the held baseline; or (a period with σ) the standardised change. */
type Mode = 'values' | 'change' | 'sigma'

interface Recorder {
  key: string
  points: LinePoint[]
  scale: DisplayColorScale
  table: Float64Array
  rec: HovmollerRecording | null
  datasetKey: string | null
  lastId: string | null
  still: boolean
  dated: boolean
  mode: Mode
  limit: number | null
  base: { field: BaselineField; values: (number | null)[] } | null
  /** A period held: each point's texel in the climatology's grid, and its means and σ per group, read once. */
  periodRef: { field: BaselineField; texels: ({ x: number; y: number } | null)[]; groups: Map<number, { mean: Float64Array; sigma: Float64Array }> } | null
  stop: (() => void) | null
  ctx: HovmollerContext
}

interface View {
  host: HTMLElement
  canvas: HTMLCanvasElement
  status: HTMLElement
  plot: HTMLElement
  now: HTMLElement
  cursor: HTMLElement
  readout: HTMLElement
  timeAxis: HTMLElement
  lut: Uint8Array
  /** AYNI (owner, 2026-10-18): when and where the diagram was highest and lowest. */
  when?: HTMLElement
}

let recorder: Recorder | null = null
let view: View | null = null
let cursor: { i: number; row: number } | null = null
/**
 * What a seek said, and when the keys were last used on the diagram: the
 * panel redraws itself when playback settles after a seek, and the reader
 * should find the message and the focus where they left them.
 */
let said: { text: string; at: number } | null = null
let keyedAt = 0

/** Read at most this often when no frame callback is available (and as a backstop). */
const POLL_MS = 100
/** In the PNG, on white: rows not played yet, and cells without data (the no-data grey over that ground). */
const UNREAD_PNG = '#f2f2f2'
const NO_DATA_PNG = '#a9a9a9'
/** A cell with no data, against the panel's dark ground. */
const NO_DATA_RGBA: readonly [number, number, number, number] = [CHANGE_GREY[0], CHANGE_GREY[1], CHANGE_GREY[2], 110]

/** AYNI (L6): the chatbot drew the line, so the diagram noted as it fills is the chatbot's until the person draws or clears a line (analyzeUI). */
let lineByAssistant = false
export function setHovmollerByAssistant(on: boolean): void {
  lineByAssistant = on
}

/** Stop recording and forget the diagram: the line went, or the dataset changed. */
export function resetHovmoller(): void {
  rowCache = null
  recorder?.stop?.()
  recorder = null
  view = null
  cursor = null
  said = null
}

/** For tests: the recording as it stands. */
export function hovmollerRecording(): HovmollerRecording | null {
  return recorder?.rec ?? null
}

/**
 * AYNI: the diagram for the methods paragraph (research tier, §Next S5):
 * how much of it was read and what it shows; null until a row is read.
 */
export function hovmollerMethodsState(): { rowsRead: number; rows: number; frames: number | null; change: boolean; against?: 'period'; sigma?: boolean } | null {
  const rec = recorder?.rec
  if (!recorder || !rec) return null
  const read = rowsRead(rec)
  if (read === 0) return null
  const field = recorder.mode !== 'values' ? baselineFor(recorder) : null
  return {
    rowsRead: read,
    rows: rec.rows,
    frames: rec.frames,
    change: !!field,
    ...(field?.period ? { against: 'period' as const, sigma: recorder.mode === 'sigma' } : {}),
  }
}

/**
 * AYNI (the chatbot's `hovmoller.summary`, 2026-10-07): the diagram as it
 * stands: the line, the window, the rows read, and the lowest, highest and
 * mean (unweighted, over the cells read) of what it shows: the values, or
 * the change from the held baseline (in σ for σ change). Null without a
 * line being recorded.
 */
export function hovmollerSummary(): import('../ayni-chat/xrChatOutcomes').ChatHovmollerSummary | null {
  const r = recorder
  const rec = r?.rec
  if (!r || !rec || rec.points.length < 2) return null
  const field = r.mode !== 'values' ? baselineFor(r) : null
  const mode = field ? r.mode : 'values'
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let n = 0
  for (let row = 0; row < rec.rows; row++) {
    if (!rec.read[row]) continue
    const ref = field ? rowBaseline(r, field, row) : null
    for (let i = 0; i < rec.points.length; i++) {
      const raw = cellValue(rec.codes[row * rec.points.length + i], r.table)
      const v = field ? cellChange(r, ref, i, raw) : raw
      if (v === null || !Number.isFinite(v)) continue
      if (v < min) min = v
      if (v > max) max = v
      sum += v
      n++
    }
  }
  const a = rec.points[0]
  const b = rec.points[rec.points.length - 1]
  const step = quantisationStep(r.scale)
  return {
    line: { from: { lat: a.lat, lon: a.lon }, to: { lat: b.lat, lon: b.lon }, lengthKm: b.distanceKm, samples: rec.points.length },
    startMs: rec.startMs,
    endMs: rec.endMs,
    dated: r.dated,
    rows: rec.rows,
    read: rowsRead(rec),
    frames: rec.frames,
    mode,
    min: n ? min : null,
    max: n ? max : null,
    mean: n ? sum / n : null,
    step,
    units: r.scale.units ?? null,
    spacingKm: rec.points.length > 1 ? b.distanceKm / (rec.points.length - 1) : 0,
    ...(() => {
      // AYNI (owner, 2026-10-18): when and where the highest and lowest were read.
      const e = hovExtremes(r)
      if (!e) return {}
      const at = (end: HovEnd) => ({ timeMs: rowTimeMs(rec, end.row), distanceKm: rec.points[end.i].distanceKm, lat: rec.points[end.i].lat, lon: rec.points[end.i].lon, others: end.others })
      return { maxAt: at(e.max), minAt: at(e.min) }
    })(),
  }
}

function lineKey(samples: readonly TransectSample[]): string {
  const a = samples[0]
  const b = samples[samples.length - 1]
  return `${samples.length}|${a.lat.toFixed(5)},${a.lon.toFixed(5)}|${b.lat.toFixed(5)},${b.lon.toFixed(5)}`
}

function button(text: string, onClick: (b: HTMLButtonElement) => void, cls = 'analyze-action'): HTMLButtonElement {
  const b = document.createElement('button')
  b.type = 'button'
  b.className = cls
  b.textContent = text
  b.addEventListener('click', () => onClick(b))
  return b
}

function para(cls: string, text: string): HTMLParagraphElement {
  const p = document.createElement('p')
  p.className = cls
  p.textContent = text
  return p
}

function where(lat: number, lon: number): string {
  // i18n-exempt: compass letters, as the pins' list and the latlng readout
  return `${formatNumber(Math.abs(lat), { maximumFractionDigits: 1 })}° ${lat >= 0 ? 'N' : 'S'}, ${formatNumber(Math.abs(lon), { maximumFractionDigits: 1 })}° ${lon >= 0 ? 'E' : 'W'}`
}

/** A time on the diagram's axis, as fine as the window needs (UTC for dates). */
function timeLabel(ms: number, spanMs: number, dated: boolean): string {
  if (!dated) {
    const s = Math.round(ms / 1000)
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
  }
  const day = 86_400_000
  const utc = { timeZone: 'UTC' } as const
  // AYNI: the frame the row holds (its stamp on the stream's calendar), never finer than the axis.
  const axis = researchFrameAxis()
  if (axis) ms = snapToFrame(ms, axis)
  if (axis?.precision === 'year' || spanMs > 20 * 365 * day) return formatDate(ms, { ...utc, year: 'numeric' })
  if (axis?.precision === 'month') return formatDate(ms, { ...utc, year: 'numeric', month: 'short' })
  if (spanMs <= 2 * day) return formatDate(ms, { ...utc, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  if (spanMs <= 400 * day) return formatDate(ms, { ...utc, month: 'short', day: 'numeric' })
  return formatDate(ms, { ...utc, year: 'numeric', month: 'short' })
}

/**
 * Whether "change from baseline" can be offered: a frame or a period held,
 * in these units, of values (not classes). A period's mean depends on each
 * row's month (and hours): each row is compared with the group of the frame
 * read into it (`rowBaseline`).
 */
function baselineFor(r: Recorder): BaselineField | null {
  const s = changeViewState()
  if (!s.field || s.other) return null
  if ((s.field.units ?? r.scale.units ?? '') !== (r.scale.units ?? '')) return null
  if (releaseOf(r.scale)?.kind === 'luma8-classified') return null
  return s.field
}

function baseValues(r: Recorder, field: BaselineField): (number | null)[] {
  if (r.base?.field !== field) r.base = { field, values: r.points.map((p) => baselineValueAt(p.lat, p.lon, field)) }
  return r.base.values
}

/**
 * AYNI S2: a period's means and σ at the line's points for one group, read
 * from the climatology once per group (NaN where it has none).
 */
function periodGroup(r: Recorder, field: BaselineField, group: number): { mean: Float64Array; sigma: Float64Array } {
  const period = field.period!
  if (r.periodRef?.field !== field) {
    const clim = period.clim
    r.periodRef = {
      field,
      texels: r.points.map((p) => {
        const uv = latLonToTexelUv(p.lat, p.lon, field.options)
        return uv ? { x: Math.min(clim.width - 1, Math.max(0, Math.floor(uv.u * clim.width))), y: Math.min(clim.height - 1, Math.max(0, Math.floor(uv.v * clim.height))) } : null
      }),
      groups: new Map(),
    }
  }
  const ref = r.periodRef
  let out = ref.groups.get(group)
  if (!out) {
    const mean = new Float64Array(r.points.length).fill(NaN)
    const sigma = new Float64Array(r.points.length).fill(NaN)
    ref.texels.forEach((at, i) => {
      if (!at) return
      const c = climatologyAt(period.clim, at.x, at.y, group)
      mean[i] = c.mean
      sigma[i] = c.sigma
    })
    out = { mean, sigma }
    ref.groups.set(group, out)
  }
  return out
}

/**
 * The baseline for a row: a held frame's values (every row alike), or the
 * period's means and σ for the group of the frame read into the row (its
 * calendar month, or month and UTC hours: `groupAt` → `frameOf`); null
 * when the row's frame cannot be placed in the climatology's calendar.
 */
function rowBaseline(r: Recorder, field: BaselineField, row: number): { mean: ArrayLike<number | null>; sigma: ArrayLike<number> | null } | null {
  if (!field.period) return { mean: baseValues(r, field), sigma: null }
  const rec = r.rec
  if (!rec) return null
  const group = field.period.groupAt(rowTimeMs(rec, row))
  return group === null ? null : periodGroup(r, field, group)
}

/** A cell as shown: the change (value − baseline), or in σ for `sigma`; null where there is none. */
function cellChange(r: Recorder, ref: { mean: ArrayLike<number | null>; sigma: ArrayLike<number> | null } | null, i: number, v: number | null): number | null {
  if (v === null || !ref) return null
  const b = ref.mean[i]
  if (b === null || !Number.isFinite(b)) return null
  if (r.mode === 'sigma') {
    const sd = ref.sigma?.[i] ?? NaN
    return sd > 0 ? (v - (b as number)) / sd : null
  }
  return v - (b as number)
}

/** The colour limit: ±3σ fixed for σ change; else the 98th percentile of what is read (`changeLimit`). */
function limitFor(r: Recorder, field: BaselineField): number | null {
  if (!r.rec) return null
  if (r.mode === 'sigma') return SIGMA_LIMIT
  return changeLimit(r.rec, r.table, (row) => rowBaseline(r, field, row)?.mean ?? null, niceLimit)
}

// --- recording ---------------------------------------------------------------

function startWatch(r: Recorder): () => void {
  let stopped = false
  const timer = window.setInterval(() => tick(r), POLL_MS)
  // Every presented frame where the browser says when one is (a frame the
  // poll would miss at playback speed is read here).
  const video = r.ctx.feed?.video?.() ?? null
  let handle: number | null = null
  if (video && typeof video.requestVideoFrameCallback === 'function') {
    const loop = (): void => {
      if (stopped) return
      tick(r)
      handle = video.requestVideoFrameCallback(loop)
    }
    handle = video.requestVideoFrameCallback(loop)
  }
  return () => {
    stopped = true
    window.clearInterval(timer)
    if (video && handle !== null && typeof video.cancelVideoFrameCallback === 'function') video.cancelVideoFrameCallback(handle)
  }
}

function tick(r: Recorder): void {
  if (recorder !== r) return
  const feed = r.ctx.feed
  const f = feed?.frame()
  if (!feed || !f) return
  if (r.datasetKey !== null && f.datasetKey !== r.datasetKey) return
  if (f.timeMs === null || f.startMs === null || f.endMs === null || !(f.endMs > f.startMs)) {
    if (!r.still) {
      r.still = true
      paintStatus()
    }
    return
  }
  r.still = false
  r.dated = f.dated
  r.datasetKey = f.datasetKey
  const window = { startMs: f.startMs, endMs: f.endMs, frames: f.frames }
  let regridded = false
  if (!r.rec) {
    r.rec = createRecording(r.points, window)
    regridded = true
  } else if (!sameWindow(r.rec, window)) {
    r.rec = regrid(r.rec, window)
    regridded = true
  }
  const rec = r.rec
  if (!rec) return
  if (regridded) paintAll()
  if (f.id === r.lastId) return
  const read = feed.codesAlong(rec.points)
  if (!read) return
  const row = recordFrame(rec, f.timeMs, read.codes)
  r.lastId = f.id
  if (row < 0) return
  if (r.mode !== 'values' && r.limit === null) {
    const field = baselineFor(r)
    if (field) r.limit = limitFor(r, field)
    if (r.limit !== null) {
      paintAll()
      return
    }
  }
  paintRow(row)
  paintStatus()
  paintNow()
}

// --- drawing -------------------------------------------------------------------

function cellRgba(r: Recorder, code: number, i: number, lut: Uint8Array, ref: ReturnType<typeof rowBaseline> | undefined): readonly [number, number, number, number] | null {
  const v = cellValue(code, r.table)
  if (r.mode !== 'values') {
    const d = cellChange(r, ref ?? null, i, v)
    if (d === null || r.limit === null) return NO_DATA_RGBA
    const [cr, cg, cb] = changeColour(d / r.limit)
    return [cr, cg, cb, 255]
  }
  if (v === null) return NO_DATA_RGBA
  const o = code * 4
  // Alpha dropped, as on the Line's strip: a value the globe draws faintly is still a value.
  return [lut[o], lut[o + 1], lut[o + 2], 255]
}

function paintRow(row: number): void {
  const r = recorder
  const v = view
  const rec = r?.rec
  if (!r || !v || !rec || !v.canvas.isConnected) return
  const ctx = v.canvas.getContext('2d')
  if (!ctx) return
  const n = rec.points.length
  const img = ctx.createImageData(n, 1)
  if (rec.read[row]) {
    const field = r.mode !== 'values' ? baselineFor(r) : null
    const ref = field ? rowBaseline(r, field, row) : undefined
    for (let i = 0; i < n; i++) {
      const c = cellRgba(r, rec.codes[row * n + i], i, v.lut, ref)
      if (!c) continue
      img.data.set(c, i * 4)
    }
  }
  ctx.putImageData(img, 0, row)
}

function paintAll(): void {
  const r = recorder
  const v = view
  const rec = r?.rec
  if (!r || !v) return
  if (!rec) {
    paintStatus()
    return
  }
  if (v.canvas.width !== rec.points.length || v.canvas.height !== rec.rows) {
    v.canvas.width = rec.points.length
    v.canvas.height = rec.rows
    v.plot.style.blockSize = `${Math.min(320, Math.max(128, rec.rows * 4))}px`
  }
  const ctx = v.canvas.getContext('2d')
  ctx?.clearRect(0, 0, v.canvas.width, v.canvas.height)
  for (let row = 0; row < rec.rows; row++) if (rec.read[row]) paintRow(row)
  paintTimeAxis()
  paintStatus()
  paintNow()
}

/**
 * AYNI (owner, 2026-10-18): the highest and lowest cell of the diagram, over
 * the rows read: what it shows (values, or their change), the row's date as
 * a button that plays from that frame, and where along the line. Rows that
 * hold the same value elsewhere are counted, not hidden.
 */
function hovExtremes(r: Recorder): { max: HovEnd; min: HovEnd } | null {
  const rec = r.rec
  if (!rec) return null
  const n = rec.points.length
  const field = r.mode !== 'values' ? baselineFor(r) : null
  // Each row's own highest and lowest are kept, and a row is read again only
  // when a frame was read into it since: a full pass over a long diagram
  // (2,048 rows of 512 points) on every frame played would cost too much.
  let c = rowCache
  if (!c || c.rec !== rec || c.mode !== r.mode || c.field !== field) {
    c = rowCache = {
      rec,
      mode: r.mode,
      field,
      seen: new Float64Array(rec.rows).fill(NaN),
      maxV: new Float64Array(rec.rows).fill(NaN),
      maxI: new Int32Array(rec.rows).fill(-1),
      minV: new Float64Array(rec.rows).fill(NaN),
      minI: new Int32Array(rec.rows).fill(-1),
    }
  }
  let max: HovEnd | null = null
  let min: HovEnd | null = null
  for (let row = 0; row < rec.rows; row++) {
    if (!rec.read[row]) continue
    if (c.seen[row] !== rec.readMs[row]) {
      c.seen[row] = rec.readMs[row]
      const ref = field ? rowBaseline(r, field, row) : null
      let hi = -Infinity
      let hiI = -1
      let lo = Infinity
      let loI = -1
      for (let i = 0; i < n; i++) {
        const raw = cellValue(rec.codes[row * n + i], r.table)
        const v = field ? cellChange(r, ref, i, raw) : raw
        if (v === null || !Number.isFinite(v)) continue
        if (v > hi) { hi = v; hiI = i }
        if (v < lo) { lo = v; loI = i }
      }
      c.maxV[row] = hi
      c.maxI[row] = hiI
      c.minV[row] = lo
      c.minI[row] = loI
    }
    if (c.maxI[row] >= 0) {
      const v = c.maxV[row]
      if (!max || v > max.value) max = { value: v, row, i: c.maxI[row], others: 0 }
      else if (v === max.value) max.others++
    }
    if (c.minI[row] >= 0) {
      const v = c.minV[row]
      if (!min || v < min.value) min = { value: v, row, i: c.minI[row], others: 0 }
      else if (v === min.value) min.others++
    }
  }
  return max && min ? { max, min } : null
}

/** Each row's highest and lowest, for the recording, mode and baseline they were read with. */
let rowCache: {
  rec: HovmollerRecording
  mode: Mode
  field: BaselineField | null
  /** The frame time read into the row when it was last scanned (NaN: not scanned). */
  seen: Float64Array
  maxV: Float64Array
  maxI: Int32Array
  minV: Float64Array
  minI: Int32Array
} | null = null

interface HovEnd { value: number; row: number; i: number; others: number }

function paintWhen(): void {
  const r = recorder
  const v = view
  const rec = r?.rec
  if (!r || !v?.when) return
  const read = rec ? rowsRead(rec) : 0
  const sig = rec ? [r.mode, r.limit, read, rec.lastRow, rec.rows].join('|') : 'none'
  fillWhenHost(v.when, sig, () => {
    if (!rec) return null
    const e = hovExtremes(r)
    if (!e) return null
    const span = rec.endMs - rec.startMs
    const text = (x: number): string => {
      if (r.mode === 'values') return formatStatValue(x, r.scale.units)
      const abs = r.mode === 'sigma' ? `${formatNumber(Math.abs(x), { maximumFractionDigits: 2 })} σ` : formatStatValue(Math.abs(x), r.scale.units)
      return `${x > 0 ? '+' : x < 0 ? '−' : ''}${abs}`
    }
    const row = (kind: 'max' | 'min', end: HovEnd) => {
      const p = rec.points[end.i]
      return {
        kind,
        label: t(kind === 'max' ? 'tools.data.when.highest' : 'tools.data.when.lowest'),
        value: text(end.value),
        time: timeLabel(rowTimeMs(rec, end.row), span, r.dated),
        ties: end.others,
        where: t('research.hov.when.along', { distance: formatStatValue(p.distanceKm, t('analyze.units.km')), where: where(p.lat, p.lon) }),
        onSeek: r.ctx.feed?.seek ? () => {
          showCursor({ i: end.i, row: end.row })
          seekRow(end.row)
        } : null,
      }
    }
    const partial = rec.frames !== null && rec.frames > rec.rows
      ? t('research.hov.when.shared')
      : read < rec.rows ? t('tools.data.when.partial', { read: formatNumber(read), total: formatNumber(rec.rows) }) : null
    return renderWhenBlock(t('research.hov.when.title'), [row('max', e.max), row('min', e.min)], partial)
  })
}

function paintStatus(): void {
  paintWhen()
  const r = recorder
  const v = view
  if (!r || !v) return
  const rec = r.rec
  if (r.still) v.status.textContent = t('research.hov.still')
  else if (!rec) v.status.textContent = t('research.hov.waiting')
  else if (rec.frames !== null && rec.frames > rec.rows) {
    v.status.textContent = t('research.hov.progressShared', { read: formatNumber(rowsRead(rec)), total: formatNumber(rec.rows), frames: formatNumber(rec.frames) })
  } else {
    v.status.textContent = t('research.hov.progress', { read: formatNumber(rowsRead(rec)), total: formatNumber(rec.rows) })
  }
}

function paintTimeAxis(): void {
  const r = recorder
  const v = view
  const rec = r?.rec
  if (!r || !v || !rec) return
  v.timeAxis.replaceChildren()
  const span = rec.endMs - rec.startMs
  const marks = rec.rows >= 5 ? [0, 0.25, 0.5, 0.75, 1] : [0, 1]
  for (const f of marks) {
    const row = Math.round(f * (rec.rows - 1))
    const label = document.createElement('span')
    label.className = 'analyze-hov-tick'
    label.style.insetBlockStart = `${((row + 0.5) / rec.rows) * 100}%`
    label.textContent = timeLabel(rowTimeMs(rec, row), span, r.dated)
    v.timeAxis.appendChild(label)
  }
}

/** The green line at the frame read last (the one on screen), as on the pins' graph. */
function paintNow(): void {
  const v = view
  const rec = recorder?.rec
  if (!v || !rec || rec.lastRow < 0) {
    if (v) v.now.hidden = true
    return
  }
  v.now.hidden = false
  v.now.style.insetBlockStart = `${((rec.lastRow + 0.5) / rec.rows) * 100}%`
}

// --- reading the diagram: pointer and keys --------------------------------------

function cellAt(e: PointerEvent | MouseEvent): { i: number; row: number } | null {
  const rec = recorder?.rec
  const v = view
  if (!rec || !v) return null
  const box = v.canvas.getBoundingClientRect()
  if (!(box.width > 0) || !(box.height > 0)) return null
  const fx = (e.clientX - box.left) / box.width
  const fy = (e.clientY - box.top) / box.height
  if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return null
  return {
    i: Math.min(rec.points.length - 1, Math.floor(fx * rec.points.length)),
    row: Math.min(rec.rows - 1, Math.floor(fy * rec.rows)),
  }
}

function describeCell(c: { i: number; row: number }): string {
  const r = recorder!
  const rec = r.rec!
  const p = rec.points[c.i]
  const distance = formatStatValue(p.distanceKm, t('analyze.units.km'))
  const time = timeLabel(rowTimeMs(rec, c.row), rec.endMs - rec.startMs, r.dated)
  if (!rec.read[c.row]) return t('research.hov.readout.unread', { distance, time })
  const v = cellValue(rec.codes[c.row * rec.points.length + c.i], r.table)
  if (v === null) return t('research.hov.readout.noData', { distance, time })
  if (r.mode !== 'values') {
    const field = baselineFor(r)
    const d = field ? cellChange(r, rowBaseline(r, field, c.row), c.i, v) : null
    if (d === null) return t('research.hov.readout.noData', { distance, time })
    const text = r.mode === 'sigma' ? `${formatNumber(Math.abs(d), { maximumFractionDigits: 2 })} σ` : formatStatValue(Math.abs(d), r.scale.units)
    return t('research.hov.readout.cell', { distance, time, value: `${d > 0 ? '+' : d < 0 ? '−' : ''}${text}` })
  }
  return t('research.hov.readout.cell', { distance, time, value: formatStatValue(v, r.scale.units) })
}

function showCursor(c: { i: number; row: number } | null): void {
  const v = view
  const rec = recorder?.rec
  cursor = c
  if (!v) return
  if (!c || !rec || c.row >= rec.rows || c.i >= rec.points.length) {
    cursor = null
    v.cursor.hidden = true
    return
  }
  v.cursor.hidden = false
  v.cursor.style.insetBlockStart = `${(c.row / rec.rows) * 100}%`
  v.cursor.style.blockSize = `max(2px, ${100 / rec.rows}%)`
  v.readout.textContent = describeCell(c)
}

function seekRow(row: number): void {
  const r = recorder
  const rec = r?.rec
  if (!r || !rec || !r.ctx.feed?.seek) return
  const ms = rowTimeMs(rec, row)
  r.ctx.feed.seek(ms)
  const text = t('research.hov.seeked', { time: timeLabel(ms, rec.endMs - rec.startMs, r.dated) })
  said = { text, at: Date.now() }
  if (view) view.readout.textContent = text
  r.ctx.announce?.(text)
}

function onKey(e: KeyboardEvent): void {
  const rec = recorder?.rec
  if (!rec) return
  keyedAt = Date.now()
  const c = cursor ?? { i: 0, row: Math.max(0, rec.lastRow) }
  const n = rec.points.length
  const page = Math.max(1, Math.round(rec.rows / 10))
  const step = Math.max(1, Math.round(n / 10))
  let next: { i: number; row: number } | null = null
  switch (e.key) {
    case 'ArrowLeft': next = { ...c, i: Math.max(0, c.i - (e.shiftKey ? step : 1)) }; break
    case 'ArrowRight': next = { ...c, i: Math.min(n - 1, c.i + (e.shiftKey ? step : 1)) }; break
    case 'ArrowUp': next = { ...c, row: Math.max(0, c.row - 1) }; break
    case 'ArrowDown': next = { ...c, row: Math.min(rec.rows - 1, c.row + 1) }; break
    case 'PageUp': next = { ...c, row: Math.max(0, c.row - page) }; break
    case 'PageDown': next = { ...c, row: Math.min(rec.rows - 1, c.row + page) }; break
    case 'Home': next = { ...c, i: 0 }; break
    case 'End': next = { ...c, i: n - 1 }; break
    case 'Enter':
    case ' ':
      e.preventDefault()
      seekRow(c.row)
      return
    default:
      return
  }
  e.preventDefault()
  showCursor(next)
}

// --- exports ---------------------------------------------------------------------

/**
 * AYNI (S8): the Line over time as a CSV for the reproducibility bundle,
 * when one is recorded; null otherwise or on a plan whose Analyze exports
 * are locked.
 */
export function hovmollerBundleCsv(): string | null {
  if (analysisExportsLocked()) return null
  return csvText()
}

function csvText(): string | null {
  const r = recorder
  const rec = r?.rec
  if (!r || !rec) return null
  const field = r.mode !== 'values' ? baselineFor(r) : null
  const period = field?.period
  return buildHovmollerCsvText(rec, r.table, {
    datasetTitle: r.ctx.datasetTitle(),
    units: r.scale.units,
    sourceUnits: r.scale.sourceUnits,
    vmin: r.scale.vmin,
    vmax: r.scale.vmax,
    step: quantisationStep(r.scale),
    dated: r.dated,
    // AYNI: each row at its frame's precision, as the pins' CSV (frameCalendar.ts).
    formatTime: (ms) => (researchFrameAxis() ? frameIsoForFile(ms) : null),
    // The diagram spans many frames: the view's own frame time is not its time.
    provenance: (r.ctx.provenance?.() ?? []).filter((row) => row[0] !== 'frame_time'),
    change: field && !period ? { base: baseValues(r, field), frameLabel: field.frameLabel } : null,
    period: field && period
      ? {
          at: (row, i, value) => cellChange(r, rowBaseline(r, field, row), i, value),
          values: r.mode === 'sigma'
            ? `standardised change from the ${period.label} climatology: (value minus the mean of the row's frame's group) / sigma of that group` // i18n-exempt: CSV row
            : `change from the ${period.label} climatology: value minus the mean of the row's frame's group`, // i18n-exempt: CSV row
          method: `${periodMethodText(period.clim.calendar.grouping)}; each row against the group of the frame read into it (its index on the time axis)${period.clim.factor > 1 ? `; the climatology keeps one texel in ${period.clim.factor} each way, so each point is compared with the mean of its cell` : ''}`, // i18n-exempt: CSV row
        }
      : null,
  })
}

/**
 * AYNI: a research record, when one is recording, notes the diagram as it
 * is exported (slice 4): the line, the window, how much was read, and the
 * range of what was read, in the dataset's units (or the change's).
 */
function trailDiagram(): void {
  const r = recorder
  const rec = r?.rec
  if (!trailOn() || !r || !rec || !rec.points.length) return
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let n = 0
  for (let row = 0; row < rec.rows; row++) {
    if (!rec.read[row]) continue
    for (let i = 0; i < rec.points.length; i++) {
      const v = cellValue(rec.codes[row * rec.points.length + i], r.table)
      if (v === null) continue
      if (v < min) min = v
      if (v > max) max = v
      sum += v
      n++
    }
  }
  const a = rec.points[0]
  const b = rec.points[rec.points.length - 1]
  const when = (ms: number) => (r.dated ? frameIsoForFile(ms) : ms / 1000)
  const units = r.scale.units ?? ''
  const read = rowsRead(rec)
  trail({
    type: 'hovmoller',
    ...(lineByAssistant ? { actor: 'assistant' as const } : {}),
    coalesce: `hovmoller|${r.key}`,
    data: {
      from: { lat: a.lat, lon: a.lon },
      to: { lat: b.lat, lon: b.lon },
      lengthKm: b.distanceKm,
      points: rec.points.length,
      rows: rec.rows,
      rowsRead: read,
      frames: rec.frames,
      start: when(rec.startMs),
      end: when(rec.endMs),
      ...(n ? { min, max, mean: sum / n } : {}),
      units,
      shown: r.mode,
      ...(r.mode !== 'values' && baselineFor(r)?.period ? { against: `${baselineFor(r)!.period!.label} climatology, each row against its frame's group` } : {}),
      method: 'Hovmöller (1949): distance along the line across, time downward; values read at the points of the Line profile, one row per frame',
      summary: `${rec.points.length} points over ${Math.round(b.distanceKm)} km, ${read} of ${rec.rows} rows read${n ? `; values ${Number(min.toPrecision(4))} to ${Number(max.toPrecision(4))} ${units}`.trimEnd() : ''}`,
    },
  })
}

/** The diagram as a picture: title, axes, colour bar and the citation, on white. */
function pngBlob(): Promise<Blob | null> {
  const r = recorder
  const v = view
  const rec = r?.rec
  if (!r || !v || !rec || typeof document === 'undefined') return Promise.resolve(null)
  const W = 1200
  const left = 150
  const right = 40
  const plotW = W - left - right
  const plotH = Math.min(900, Math.max(300, rec.rows * 3))
  const top = 90
  const H = top + plotH + 190
  const out = document.createElement('canvas')
  out.width = W
  out.height = H
  const g = out.getContext('2d')
  if (!g) return Promise.resolve(null)
  g.fillStyle = '#ffffff'
  g.fillRect(0, 0, W, H)
  g.fillStyle = '#111111'
  g.font = '600 22px system-ui, sans-serif'
  g.fillText(r.ctx.datasetTitle() ?? '', left, 36)
  g.font = '15px system-ui, sans-serif'
  const a = rec.points[0]
  const b = rec.points[rec.points.length - 1]
  g.fillText(`${t('research.hov.title')} · ${where(a.lat, a.lon)} → ${where(b.lat, b.lon)}`, left, 62)
  g.imageSmoothingEnabled = false
  // Rows not played yet stay this pale ground; cells without data are the darker grey.
  g.fillStyle = UNREAD_PNG
  g.fillRect(left, top, plotW, plotH)
  g.drawImage(v.canvas, left, top, plotW, plotH)
  g.fillStyle = '#111111'
  g.strokeStyle = '#111111'
  g.strokeRect(left + 0.5, top + 0.5, plotW - 1, plotH - 1)
  // Time down the left, first frame at the top.
  g.font = '14px system-ui, sans-serif'
  g.textAlign = 'right'
  const span = rec.endMs - rec.startMs
  for (const f of [0, 0.25, 0.5, 0.75, 1]) {
    const row = Math.round(f * (rec.rows - 1))
    const y = top + ((row + 0.5) / rec.rows) * plotH
    g.fillText(timeLabel(rowTimeMs(rec, row), span, r.dated), left - 10, y + 5)
  }
  // Distance along the bottom.
  const total = b.distanceKm
  for (const f of [0, 0.5, 1]) {
    g.textAlign = f === 0 ? 'left' : f === 1 ? 'right' : 'center'
    g.fillText(formatStatValue(f * total, t('analyze.units.km')), left + f * plotW, top + plotH + 22)
  }
  g.textAlign = 'center'
  g.fillText(t('research.hov.distanceAxis'), left + plotW / 2, top + plotH + 44)
  // Colour bar.
  const barY = top + plotH + 66
  const barW = Math.min(500, plotW)
  const field = r.mode !== 'values' ? baselineFor(r) : null
  const grad = g.createLinearGradient(left, 0, left + barW, 0)
  if (field && r.limit !== null) {
    CHANGE_PALETTE.forEach((c, i) => grad.addColorStop(i / (CHANGE_PALETTE.length - 1), `rgb(${c[0]}, ${c[1]}, ${c[2]})`))
  } else {
    for (const s of displayGradientStops(r.scale, r.ctx.display())) grad.addColorStop(s.position, `rgb(${s.rgba[0]}, ${s.rgba[1]}, ${s.rgba[2]})`)
  }
  g.fillStyle = grad
  g.fillRect(left, barY, barW, 16)
  g.strokeRect(left + 0.5, barY + 0.5, barW - 1, 15)
  g.fillStyle = '#111111'
  const [lo, hi] = field && r.limit !== null ? [-r.limit, r.limit] : [r.scale.vmin, r.scale.vmax]
  const barText = (x: number) => (field && r.mode === 'sigma' ? `${x > 0 ? '+' : x < 0 ? '−' : ''}${formatNumber(Math.abs(x))} σ` : formatStatValue(x, r.scale.units))
  g.textAlign = 'left'
  g.fillText(barText(lo), left, barY + 36)
  g.textAlign = 'right'
  g.fillText(barText(hi), left + barW, barY + 36)
  g.textAlign = 'left'
  // The two kinds of empty, as in the panel.
  let kx = left + barW + 40
  for (const [fill, label] of [[UNREAD_PNG, t('research.hov.notRead')], [NO_DATA_PNG, t('research.hov.noData')]] as const) {
    g.fillStyle = fill
    g.fillRect(kx, barY, 22, 16)
    g.strokeRect(kx + 0.5, barY + 0.5, 21, 15)
    g.fillStyle = '#111111'
    g.fillText(label, kx + 30, barY + 13)
    kx += 30 + g.measureText(label).width + 30
  }
  g.font = '12px system-ui, sans-serif'
  const cite = r.ctx.provenance?.()?.find((row) => row[0] === 'cite_as')?.[1]
  const foot = [t('research.hov.timeDown'), typeof cite === 'string' ? cite : ''].filter(Boolean)
  foot.forEach((line, k) => g.fillText(line.length > 180 ? `${line.slice(0, 177)}…` : line, left, barY + 64 + k * 18))
  return new Promise((resolve) => out.toBlob((blob) => resolve(blob), 'image/png'))
}

function download(blob: Blob, filename: string): void {
  if (typeof URL.createObjectURL !== 'function') return
  // AYNI: a research record, when one is recording, keeps the file's name and SHA-256 (slice 4).
  trailFile({ name: filename, body: blob, kind: 'png' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// --- the section -------------------------------------------------------------------

/**
 * Draw the section into `host` (the Profiles tab, under the Line). Called
 * whenever the Line section redraws; the recording outlives redraws for as
 * long as the line is the same.
 */
export function renderHovmollerSection(host: HTMLElement, line: HovmollerLine | null, ctx: HovmollerContext): void {
  host.replaceChildren()
  view = null
  if (!researchToolsAllowed()) {
    resetHovmoller()
    // A class borrowing the data tools sees nothing; a plan with them sees the lock.
    if (!researchUpsellShown()) {
      host.hidden = true
      return
    }
    host.hidden = false
    const locked = button(t('research.hov.title'), (b) => openPlans(b, 'research', { notice: t('plans.locked.hovmoller') }))
    locked.classList.add('analyze-hov-open', 'analyze-locked')
    locked.setAttribute('aria-describedby', 'analyze-hov-lock')
    const why = para('analyze-lock-note', t('research.hov.locked'))
    why.id = 'analyze-hov-lock'
    host.append(locked, why)
    return
  }
  host.hidden = false
  host.appendChild(head())
  if (!line || line.samples.length < 2 || !ctx.feed) {
    resetHovmoller()
    host.appendChild(para('analyze-message', t('research.hov.noLine')))
    return
  }

  const key = lineKey(line.samples)
  if (!recorder || recorder.key !== key) {
    resetHovmoller()
    const r: Recorder = {
      key,
      points: line.samples.map((s) => ({ lat: s.lat, lon: s.lon, distanceKm: s.distanceKm })),
      scale: line.scale,
      table: lineValueTable(line.scale),
      rec: null,
      datasetKey: null,
      lastId: null,
      still: false,
      dated: false,
      mode: 'values',
      limit: null,
      base: null,
      periodRef: null,
      stop: null,
      ctx,
    }
    recorder = r
    r.stop = startWatch(r)
  }
  const r = recorder
  r.ctx = ctx
  {
    const held = baselineFor(r)
    if ((r.mode !== 'values' && !held) || (r.mode === 'sigma' && !held?.period?.clim.hasSigma)) {
      r.mode = 'values'
      r.limit = null
    }
  }

  host.appendChild(para('analyze-coverage analyze-hov-lead', t('research.hov.lead')))
  const status = para('analyze-coverage analyze-hov-status', '')
  status.setAttribute('aria-live', 'polite')
  host.appendChild(status)

  // The figure: time labels | plot, then distances and the line's ends.
  const figure = document.createElement('div')
  figure.className = 'analyze-hov-figure'
  const timeAxis = document.createElement('div')
  timeAxis.className = 'analyze-hov-time'
  timeAxis.setAttribute('aria-hidden', 'true')
  const plot = document.createElement('div')
  plot.className = 'analyze-hov-plot'
  plot.tabIndex = 0
  plot.setAttribute('role', 'group')
  plot.setAttribute('aria-roledescription', t('research.hov.roledescription'))
  plot.setAttribute('aria-label', t('research.hov.aria'))
  const canvas = document.createElement('canvas')
  canvas.className = 'analyze-hov-canvas'
  canvas.width = 1
  canvas.height = 1
  canvas.setAttribute('aria-hidden', 'true')
  const now = document.createElement('div')
  now.className = 'analyze-hov-now'
  now.hidden = true
  const cursorEl = document.createElement('div')
  cursorEl.className = 'analyze-hov-cursor'
  cursorEl.hidden = true
  plot.append(canvas, now, cursorEl)
  const spacer = document.createElement('div')
  const distance = document.createElement('div')
  distance.className = 'analyze-hov-distance'
  distance.setAttribute('aria-hidden', 'true')
  const total = r.points[r.points.length - 1].distanceKm
  for (const f of [0, 0.5, 1]) {
    const s = document.createElement('span')
    s.textContent = formatStatValue(f * total, t('analyze.units.km'))
    distance.appendChild(s)
  }
  const spacer2 = document.createElement('div')
  const ends = document.createElement('div')
  ends.className = 'analyze-hov-ends'
  const a = r.points[0]
  const b = r.points[r.points.length - 1]
  ends.append(
    Object.assign(document.createElement('span'), { textContent: t('research.hov.from', { where: where(a.lat, a.lon) }) }),
    Object.assign(document.createElement('span'), { textContent: t('research.hov.to', { where: where(b.lat, b.lon) }) }),
  )
  figure.append(timeAxis, plot, spacer, distance, spacer2, ends)
  host.appendChild(figure)

  const readout = para('analyze-hov-readout', t('research.hov.readoutHint'))
  readout.id = 'analyze-hov-readout'
  readout.setAttribute('role', 'status')
  plot.setAttribute('aria-describedby', 'analyze-hov-readout')
  host.appendChild(readout)
  const when = document.createElement('div')
  when.className = 'analyze-hov-when'
  when.hidden = true
  host.appendChild(when)

  // The colour bar and the keys for the two kinds of empty.
  const field = baselineFor(r)
  const changeOn = r.mode !== 'values' && !!field
  const bar = document.createElement('div')
  bar.className = 'analyze-hov-bar'
  const ramp = document.createElement('div')
  ramp.className = 'analyze-hov-ramp'
  const stops = changeOn
    ? CHANGE_PALETTE.map((c, i) => `rgb(${c[0]}, ${c[1]}, ${c[2]}) ${((i / (CHANGE_PALETTE.length - 1)) * 100).toFixed(1)}%`)
    : displayGradientStops(r.scale, ctx.display()).map((s) => `rgb(${s.rgba[0]}, ${s.rgba[1]}, ${s.rgba[2]}) ${(s.position * 100).toFixed(1)}%`)
  ramp.style.backgroundImage = `linear-gradient(to right, ${stops.join(', ')})`
  const [lo, hi] = changeOn ? (r.limit !== null ? [-r.limit, r.limit] : [NaN, NaN]) : [r.scale.vmin, r.scale.vmax]
  const inSigma = changeOn && r.mode === 'sigma'
  const loText = inSigma ? `−${formatNumber(Math.abs(lo))} σ` : formatStatValue(lo, r.scale.units)
  const hiText = inSigma ? `+${formatNumber(Math.abs(hi))} σ` : formatStatValue(hi, r.scale.units)
  ramp.setAttribute('role', 'img')
  ramp.setAttribute('aria-label', t('research.hov.barAria', { min: loText, max: hiText }))
  const labels = document.createElement('div')
  labels.className = 'analyze-hov-bar-labels'
  labels.append(
    Object.assign(document.createElement('span'), { textContent: loText }),
    Object.assign(document.createElement('span'), { textContent: hiText }),
  )
  const keys = document.createElement('div')
  keys.className = 'analyze-hov-keys'
  for (const [cls, text] of [['analyze-hov-key-unread', t('research.hov.notRead')], ['analyze-hov-key-nodata', t('research.hov.noData')]] as const) {
    const k = document.createElement('span')
    k.className = 'analyze-hov-key'
    const sw = document.createElement('span')
    sw.className = `analyze-hov-swatch ${cls}`
    sw.setAttribute('aria-hidden', 'true')
    k.append(sw, document.createTextNode(text))
    keys.appendChild(k)
  }
  bar.append(ramp, labels, keys)
  host.appendChild(bar)

  // Values, or the change from a held baseline.
  if (field) {
    const group = document.createElement('div')
    group.className = 'analyze-change-modes analyze-hov-modes'
    group.setAttribute('role', 'group')
    group.setAttribute('aria-label', t('research.hov.showAria'))
    const modes: Array<[Mode, string]> = [['values', t('research.hov.values')], ['change', t('research.hov.change')]]
    if (field.period?.clim.hasSigma) modes.push(['sigma', t('research.hov.sigma')])
    for (const [mode, text] of modes) {
      const btn = button(text, () => {
        if (r.mode === mode) return
        r.mode = mode
        r.limit = mode !== 'values' ? limitFor(r, field) : null
        renderHovmollerSection(host, line, ctx)
        host.querySelector<HTMLElement>(`.analyze-hov-mode[data-value="${mode}"]`)?.focus()
      }, 'analyze-action analyze-change-mode analyze-hov-mode')
      btn.dataset.value = mode
      btn.setAttribute('aria-pressed', String(r.mode === mode))
      group.appendChild(btn)
    }
    host.appendChild(group)
    if (changeOn && field.period) {
      const cal = field.period.clim.calendar
      host.appendChild(para('analyze-coverage analyze-hov-change-note', r.mode === 'sigma'
        ? t('research.hov.sigmaNote', { span: field.period.label })
        : r.limit !== null
          ? t('research.hov.periodChangeNote', { span: field.period.label, units: r.scale.units ?? '—', limit: formatStatValue(r.limit, r.scale.units) })
          : t('research.hov.changeWaiting')))
      if (cal.grouping === 'subdaily') host.appendChild(para('analyze-coverage analyze-hov-period-hours', t('research.hov.periodHours')))
      if (field.period.clim.factor > 1) host.appendChild(para('analyze-coverage analyze-hov-period-thinned', t('research.hov.periodThinned', { factor: String(field.period.clim.factor) })))
    } else if (changeOn) {
      host.appendChild(para('analyze-coverage analyze-hov-change-note', r.limit !== null
        ? t('research.hov.changeNote', { frame: field.frameLabel ?? t('research.baseline.noFrame'), units: r.scale.units ?? '—', limit: formatStatValue(r.limit, r.scale.units) })
        : t('research.hov.changeWaiting')))
    }
  }

  // What the numbers are worth.
  const n = r.points.length
  const spacing = formatStatValue(n > 1 ? total / (n - 1) : 0, t('analyze.units.km'))
  const step = quantisationStep(r.scale)
  host.appendChild(para('analyze-coverage analyze-hov-time-note', t('research.hov.timeDown')))
  host.appendChild(para('analyze-coverage analyze-hov-limits', step !== null
    ? t('research.hov.limits', { spacing, step: formatStatValue(step, r.scale.units) })
    : t('research.hov.limitsUneven', { spacing })))

  const actions = document.createElement('div')
  actions.className = 'analyze-hov-actions'
  actions.appendChild(button(t('research.hov.restart'), () => {
    if (!recorder) return
    recorder.rec = null
    recorder.lastId = null
    recorder.limit = null
    tick(recorder)
    paintAll()
  }, 'analyze-action analyze-hov-restart'))
  host.appendChild(actions)
  if (!analysisExportsLocked()) {
    const csv = button(t('research.hov.csv'), () => {
      const text = csvText()
      if (text) trailDiagram()
      if (text) downloadCsv(ayniCsvFilename('hovmoller', ctx.datasetTitle(), new Date()), text)
    }, 'analyze-export analyze-hov-csv')
    const png = button(t('research.hov.png'), () => {
      void pngBlob().then((blob) => {
        if (blob) trailDiagram()
        if (blob) download(blob, ayniCsvFilename('hovmoller', ctx.datasetTitle(), new Date()).replace(/\.csv$/, '.png'))
      })
    }, 'analyze-export analyze-hov-png')
    host.append(csv, png)
  }

  view = { host, canvas, status, plot, now, cursor: cursorEl, readout, timeAxis, lut: buildDisplayLut(r.scale, ctx.display()), when }

  plot.addEventListener('pointermove', (e) => {
    const c = cellAt(e)
    if (c) showCursor(c)
  })
  plot.addEventListener('pointerleave', () => {
    if (document.activeElement !== plot) showCursor(null)
  })
  plot.addEventListener('click', (e) => {
    const c = cellAt(e)
    if (!c) return
    showCursor(c)
    seekRow(c.row)
  })
  plot.addEventListener('keydown', onKey)
  plot.addEventListener('focus', () => {
    const rec = recorder?.rec
    if (rec && !cursor) showCursor({ i: 0, row: Math.max(0, rec.lastRow) })
  })

  paintAll()
  tick(r)
  // A redraw (playback settled) keeps the cell, the message and the keyboard focus.
  if (cursor) showCursor(cursor)
  if (said && Date.now() - said.at < 4000) readout.textContent = said.text
  if (Date.now() - keyedAt < 10_000 && (!document.activeElement || document.activeElement === document.body)) plot.focus({ preventScroll: true })
}

function head(): HTMLElement {
  const head = document.createElement('div')
  head.className = 'analyze-transect-head'
  const h = document.createElement('h3')
  h.textContent = t('research.hov.title')
  head.appendChild(h)
  return head
}
